import type { CategoryIcon } from '@k2pos/core';

/**
 * The app's icon set, drawn inline.
 *
 * The RN screens used Unicode glyphs (⌂ ▬ ◎ ☏ △ …) which resolved through the
 * loaded Noto Sans Lao or the system font. In a WebView that is a liability: the
 * subset font does not cover them, so they fall through to whatever the device
 * picks, and several — ☏ and △ especially — render with emoji presentation on
 * some Android builds: colour, wrong metrics, wrong baseline. It looks correct
 * on a dev Pixel and wrong on the shop's phone.
 *
 * Every icon inherits `currentColor` and sizes from the `size` prop, so callers
 * style them exactly like text.
 */

export interface IconProps {
  size?: number;
  className?: string;
  /** Decorative by default; pass a label when the icon is the only content. */
  label?: string;
}

function svgProps({ size = 20, className, label }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    className,
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    ...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true }),
  };
}

export function IconHome(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20h13V9.5" />
    </svg>
  );
}

export function IconPlus(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function IconMinus(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M5 12h14" />
    </svg>
  );
}

export function IconList(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

export function IconMore(p: IconProps) {
  return (
    <svg {...svgProps(p)} strokeWidth={2.4}>
      <path d="M6 12h.01M12 12h.01M18 12h.01" />
    </svg>
  );
}

/** The grid/list layout toggle on the Pick screen. */
export function IconGrid(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    </svg>
  );
}

export function IconRows(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <rect x="3.5" y="4.5" width="17" height="5" rx="1.5" />
      <rect x="3.5" y="14.5" width="17" height="5" rx="1.5" />
    </svg>
  );
}

export function IconSearch(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </svg>
  );
}

export function IconPhone(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.5 5.7 2 2 0 0 1 6.5 3.5Z" />
    </svg>
  );
}

export function IconChevronLeft(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="m14.5 5-7 7 7 7" />
    </svg>
  );
}

export function IconChevronRight(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="m9.5 5 7 7-7 7" />
    </svg>
  );
}

export function IconBackspace(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M9 5h10a1.5 1.5 0 0 1 1.5 1.5v11A1.5 1.5 0 0 1 19 19H9l-6-7Z" />
      <path d="m12 9.5 5 5M17 9.5l-5 5" />
    </svg>
  );
}

export function IconCheck(p: IconProps) {
  return (
    <svg {...svgProps(p)} strokeWidth={2.4}>
      <path d="m4.5 12.5 5 5 10-11" />
    </svg>
  );
}

export function IconClose(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/* ---- Category icons ------------------------------------------------------ */

/** ທັງໝົດ — all categories. */
function IconCatGrid(p: IconProps) {
  return <IconGrid {...p} />;
}

/** ຊີມັງ — a cement bag. */
function IconCatBag(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M7 6.5h10l1.5 13h-13Z" />
      <path d="M9.5 3.5h5v3h-5Z" />
    </svg>
  );
}

/** ເຫຼັກ — rebar / bar stock. */
function IconCatBar(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <rect x="3" y="8.5" width="18" height="3.2" rx="1.6" />
      <rect x="3" y="14" width="18" height="3.2" rx="1.6" />
    </svg>
  );
}

/** ຊາຍ/ຫີນ — a pile of sand or gravel. */
function IconCatAggregate(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M2.5 19h19L14 6.5 10 13l-2.5-3Z" />
    </svg>
  );
}

/** ທໍ່ — pipe, seen end-on. */
function IconCatPipe(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.8" />
    </svg>
  );
}

const CATEGORY_COMPONENTS: Record<CategoryIcon, (p: IconProps) => React.ReactElement> = {
  grid: IconCatGrid,
  bag: IconCatBag,
  bar: IconCatBar,
  aggregate: IconCatAggregate,
  pipe: IconCatPipe,
};

/** Resolves the semantic key from `CATEGORY_ICONS` to artwork. */
export function CategoryGlyph({ icon, ...rest }: IconProps & { icon: CategoryIcon }) {
  const Component = CATEGORY_COMPONENTS[icon] ?? IconCatGrid;
  return <Component {...rest} />;
}

export function IconGear(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.4M12 18.8v2.4M21.2 12h-2.4M5.2 12H2.8M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7M18.5 18.5l-1.7-1.7M7.2 7.2 5.5 5.5" />
    </svg>
  );
}

/** ຮັບເຄື່ອງເຂົ້າຮ້ານ — a delivery lorry. */
export function IconTruck(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M2.5 6.5h10v9h-10Z" />
      <path d="M12.5 10h4l3 3v2.5h-7Z" />
      <circle cx="7" cy="17.5" r="1.8" />
      <circle cx="16" cy="17.5" r="1.8" />
    </svg>
  );
}

/** ລາຍຈ່າຍ — a wallet. */
export function IconWallet(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M3.5 7.5h15a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5h-15Z" />
      <path d="M3.5 7.5v-1a1.5 1.5 0 0 1 1.5-1.5h11" />
      <circle cx="16.5" cy="13" r="1.2" />
    </svg>
  );
}

/** ກຳໄລ ຂາດທຶນ — a bar chart. */
export function IconChart(p: IconProps) {
  return (
    <svg {...svgProps(p)}>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <rect x="7" y="12" width="3" height="5" rx="1" />
      <rect x="12.5" y="8" width="3" height="9" rx="1" />
      <rect x="18" y="14" width="3" height="3" rx="1" />
    </svg>
  );
}
